import { useHistory, useLocation } from 'react-router-dom';
import { TabNavigationProps, TabProps } from '@cbhq/cds-common';
import { zIndex } from '@cbhq/cds-common/tokens/zIndex';
import { Box } from '@cbhq/cds-web/layout';
import { TabNavigation } from '@cbhq/cds-web/tabs';

/**
 * Represents a tab with routing information.
 */
interface RoutedTab extends TabProps {
  /** Route path for the tab */
  route: string;
}

/**
 * Props for the RoutedTopNavTabs component.
 */
interface RoutedTopNavTabsProps extends Omit<TabNavigationProps, 'tabs' | 'value' | 'onChange'> {
  /** Array of RoutedTab objects */
  tabs: RoutedTab[];
}

/**
 * Renders a top navigation bar with tabs that change the URL when clicked.
 * Assumes that the component is rendered within route that exists in tabs.
 *
 * @param props.tabs - An array of RoutedTab objects defining the tabs and their routes
 * @returns A React component that displays routed top navigation tabs
 */
export function RoutedTopNavTabs({ tabs }: RoutedTopNavTabsProps) {
  const history = useHistory();
  const location = useLocation();

  const currentTab = tabs.find((tab) => tab.route === location.pathname) || tabs[0];

  const handleChange = (tabId: string) => {
    const tab = tabs.find((t) => t.id === tabId);
    if (tab) {
      history.push(tab.route);
    }
  };

  return (
    <Box
      spacingStart={1}
      borderedBottom
      width="100%"
      position="relative"
      top="-2px"
      background="background"
      zIndex={zIndex.navigation}
    >
      <TabNavigation
        value={currentTab.id}
        tabs={tabs}
        onChange={handleChange}
        spacingHorizontal={5}
      />
    </Box>
  );
}
